import React from 'react';
import { usePortalStore } from '../store';
import { useAuthStore } from '../store/useAuthStore';
import { Zap, Users, List, Sun, Moon, ChevronDown, Search, Brain } from 'lucide-react';
import type { PortalMode } from './Shell';

interface TopBarProps {
  panelOpen: boolean;
  onTogglePanel: () => void;
  mode: PortalMode;
  onToggleMode: () => void;
}

export const TopBar: React.FC<TopBarProps> = ({ panelOpen, onTogglePanel, mode, onToggleMode }) => {
  const { company, waveStatus, actionItems } = usePortalStore();
  const { user } = useAuthStore();

  const companyLabel = company?.name ?? user?.company_name ?? user?.zpc_id ?? '';
  const contextPct = company?.contextUsagePct ?? 0;

  return (
    <div className="zpc-topbar px-6 py-3">
      <div className="flex items-center justify-between">
        {/* Resource bar — game ref: RTS resource bar (Settlers, AoE, Civ) */}
        <div className="flex items-center gap-6">
          {company && (
            <>
              {/* Tokens */}
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 accent-amber" style={{ color: 'var(--accent-amber)' }} />
                <div className="flex flex-col">
                  <span className="resource-label">Tokens</span>
                  <span className="resource-value">{(company.tokenBurnRate / 1000).toFixed(1)}k/wk</span>
                </div>
              </div>

              {/* Agents */}
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" style={{ color: 'var(--accent-teal)' }} />
                <div className="flex flex-col">
                  <span className="resource-label">Agents</span>
                  <span className="resource-value">{waveStatus?.epics.length ?? 0} Active</span>
                </div>
              </div>

              {/* Queue */}
              <div className="flex items-center gap-2">
                <List className="w-4 h-4" style={{ color: 'var(--accent-teal)' }} />
                <div className="flex flex-col">
                  <span className="resource-label">Queue</span>
                  <span className="resource-value">{actionItems.length} Items</span>
                </div>
              </div>

              {/* Context usage */}
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4" style={{ color: contextPct >= 85 ? '#E05050' : 'var(--accent-teal)' }} />
                <div className="flex flex-col">
                  <span className="resource-label">Context</span>
                  <span className="resource-value" style={{ color: contextPct >= 85 ? '#E05050' : contextPct >= 70 ? 'var(--accent-amber)' : 'var(--text-primary)' }}>
                    {contextPct.toFixed(1)}%
                  </span>
                </div>
              </div>

              {/* Health bar */}
              <div className="flex flex-col gap-1">
                <span className="resource-label">Health</span>
                <div className="health-track">
                  <div className="health-fill" style={{ width: `${company.health}%` }} />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Company badge */}
        {companyLabel && (
          <div
            className="px-3 py-1 rounded-md text-sm font-semibold truncate max-w-[12rem]"
            style={{
              backgroundColor: 'var(--active-overlay)',
              color: 'var(--accent-amber)',
              border: '1px solid var(--border-default)',
              fontFamily: 'var(--font-ui)',
            }}
            title={companyLabel}
          >
            {companyLabel}
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center gap-3">
          {/* Space / Sand mode toggle */}
          <button
            onClick={onToggleMode}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors"
            style={{
              backgroundColor: 'var(--active-overlay)',
              border: '1px solid var(--border-default)',
              color: 'var(--accent-amber)',
              fontFamily: 'var(--font-ui)',
              fontSize: '0.8125rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
            title={mode === 'space' ? 'Switch to Sand mode (daylight)' : 'Switch to Space mode (night)'}
          >
            {mode === 'space'
              ? <Sun className="w-3.5 h-3.5" />
              : <Moon className="w-3.5 h-3.5" />
            }
            <span>{mode === 'space' ? 'Space' : 'Sand'}</span>
          </button>

          {/* Search */}
          <div className="relative hidden md:block">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none"
              style={{ color: 'var(--text-tertiary)' }}
            />
            <input
              type="text"
              placeholder="Search..."
              className="search-input pl-9 pr-4 py-1.5"
              style={{ width: '11rem' }}
            />
          </div>

          {/* AI panel toggle */}
          <button
            onClick={onTogglePanel}
            className="p-1.5 rounded-md transition-colors focus-ring"
            style={{
              backgroundColor: panelOpen ? 'var(--active-overlay)' : 'transparent',
              color: panelOpen ? 'var(--accent-amber)' : 'var(--text-tertiary)',
              border: '1px solid',
              borderColor: panelOpen ? 'var(--accent-amber)' : 'var(--border-subtle)',
              cursor: 'pointer',
            }}
            title={panelOpen ? 'Hide AI Panel' : 'Show AI Panel'}
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
